// Pure decision logic for the app-update flow. ZERO imports and no browser
// globals, so scripts/probe-app-update.mjs can exercise every branch under
// plain Node. The browser wiring lives in ./app-update.ts; the busy/idle
// registry lives in ./update-safety.ts.
//
// Model (the service worker keeps skipWaiting + clients.claim, so a new worker
// never waits): "this document is stale" means the build the server now hands
// out DIFFERS from the build this document is running. Differs, not newer, so
// a rollback updates clients too. Applying an update is a plain reload at a
// safe moment; nothing is ever sent to the worker.

/** Background time after which an idle stale page may reload silently. */
export const HIDDEN_APPLY_MS = 10 * 60_000;
/** Focus/visibility checks are debounced to at most one per this window. */
export const CHECK_MIN_INTERVAL_MS = 30_000;
/** Periodic check cadence while the page stays visible. */
export const CHECK_PERIOD_MS = 60 * 60_000;
/** At most one automatic reload per target inside this window. */
export const RELOAD_GUARD_MS = 120_000;
/** After this many automatic reloads for one target, only the banner remains. */
export const MAX_AUTO_RELOADS = 3;
/**
 * Silent apply is the default; the banner is the exception. A stale but
 * working page is only offered the banner after it has been stale this long
 * without ever getting a quiet moment to update by itself. A failed chunk
 * (urgent) or a required update shows it immediately.
 */
export const BANNER_AFTER_STALE_MS = 4 * 60 * 60_000;

// sessionStorage only, never adia-* and never localStorage: owner-safety
// treats adia-* localStorage keys as protected user state.
export const RELOAD_GUARD_KEY = 'whw-update-reload-v1';
export const DISMISS_KEY = 'whw-update-dismissed-v1';
export const RETURN_KEY = 'whw-update-return-v1';
/** Test hosts only (see resolveThresholds): remembered shortened thresholds. */
export const TEST_THRESHOLDS_KEY = 'whw-update-test-v1';
/** Test hosts only: `?whw-update-test=hidden:6,banner:20` (seconds). */
export const TEST_THRESHOLDS_PARAM = 'whw-update-test';

export const UNSET_BUILD = 'dev-unset';

export interface Thresholds {
  /** Background time after which an idle stale page may reload silently. */
  hiddenApplyMs: number;
  /** How long a visible page stays stale before the line is offered. */
  bannerAfterStaleMs: number;
}

export const DEFAULT_THRESHOLDS: Thresholds = Object.freeze({
  hiddenApplyMs: HIDDEN_APPLY_MS,
  bannerAfterStaleMs: BANNER_AFTER_STALE_MS,
});

const TEST_THRESHOLD_FLOOR_MS = 2_000;

/**
 * Hosts where the two waiting periods may be shortened for testing: local
 * development and Vercel's own *.vercel.app deployment hosts. An allowlist,
 * not "anything but production", so no public domain (watchhim.work, www, or
 * any future custom domain) can ever honour the flag.
 */
export function isUpdateTestHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.vercel.app');
}

function testSeconds(text: string, name: string): number | null {
  const match = new RegExp(`(?:^|,)${name}:([0-9]{1,5})(?:,|$)`).exec(text);
  return match ? Number(match[1]) : null;
}

/**
 * `hidden:<seconds>,banner:<seconds>` (either part optional). Values can only
 * shorten a waiting period, never extend it, and never go below two seconds.
 */
export function parseTestThresholds(text: string | null): Thresholds | null {
  if (!text || text.length > 64) return null;
  const hidden = testSeconds(text, 'hidden');
  const banner = testSeconds(text, 'banner');
  if (hidden === null && banner === null) return null;
  const within = (seconds: number | null, limit: number): number =>
    seconds === null ? limit : Math.min(limit, Math.max(TEST_THRESHOLD_FLOOR_MS, seconds * 1000));
  return {
    hiddenApplyMs: within(hidden, HIDDEN_APPLY_MS),
    bannerAfterStaleMs: within(banner, BANNER_AFTER_STALE_MS),
  };
}

function testParam(search: string): string | null {
  const query = search.startsWith('?') ? search.slice(1) : search;
  for (const pair of query.split('&')) {
    const at = pair.indexOf('=');
    const name = at === -1 ? pair : pair.slice(0, at);
    if (name !== TEST_THRESHOLDS_PARAM) continue;
    try {
      return decodeURIComponent(at === -1 ? '' : pair.slice(at + 1));
    } catch {
      return '';
    }
  }
  return null;
}

export interface ThresholdResolution {
  thresholds: Thresholds;
  /** Value to remember for this tab, or null to remember nothing new. */
  store: string | null;
  /** Remove any remembered value. */
  clear: boolean;
}

/**
 * The waiting periods this document uses. Everywhere except a test host the
 * answer is always the defaults, the URL flag is ignored, and a remembered
 * value is removed. On a test host the URL flag wins and is remembered for the
 * tab (so it survives the update reload and in-app navigation); any other
 * value of the flag, e.g. `off`, forgets it.
 */
export function resolveThresholds(
  hostname: string,
  search: string,
  stored: string | null,
): ThresholdResolution {
  if (!isUpdateTestHost(hostname)) {
    return { thresholds: DEFAULT_THRESHOLDS, store: null, clear: stored !== null };
  }
  const fromUrl = testParam(search);
  if (fromUrl !== null) {
    const parsed = parseTestThresholds(fromUrl);
    return parsed
      ? { thresholds: parsed, store: fromUrl, clear: false }
      : { thresholds: DEFAULT_THRESHOLDS, store: null, clear: true };
  }
  const remembered = parseTestThresholds(stored);
  return remembered
    ? { thresholds: remembered, store: null, clear: false }
    : { thresholds: DEFAULT_THRESHOLDS, store: null, clear: stored !== null };
}

export interface VersionInfo {
  build: string;
  builtAt: number;
  minBuiltAt: number;
}

export interface RunningBuild {
  /** VITE_BUILD_ID (40-hex) or 'dev-unset'. */
  buildId: string;
  /** Build-time epoch ms compiled into the bundle; 0 when unknown. */
  builtAt: number;
}

function isEpoch(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/**
 * Strict /version.json validation. A missing file is answered by the SPA
 * rewrite with index.html (HTTP 200, text/html), so anything that is not
 * well-formed JSON of the exact shape is "no information", never an update.
 */
export function parseVersionInfo(
  contentType: string | null,
  body: string,
): VersionInfo | null {
  if (!contentType || !/\bjson\b/i.test(contentType)) return null;
  let value: unknown;
  try {
    value = JSON.parse(body);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const { build, builtAt, minBuiltAt } = record;
  if (typeof build !== 'string') return null;
  if (build !== UNSET_BUILD && !/^[0-9a-f]{40}$/.test(build)) return null;
  if (!isEpoch(builtAt) || !isEpoch(minBuiltAt)) return null;
  return { build, builtAt, minBuiltAt };
}

export type Staleness =
  | { stale: false }
  | { stale: true; forced: boolean; target: string };

/**
 * Compare the served build with the running document.
 *
 * Forced requires a genuinely different served build that itself satisfies
 * the floor. A misconfigured floor above the served build could never be met
 * by reloading, so it is ignored instead of looping.
 */
export function compareVersion(running: RunningBuild, served: VersionInfo): Staleness {
  const buildDiffers =
    running.buildId !== UNSET_BUILD &&
    served.build !== UNSET_BUILD &&
    served.build !== running.buildId;
  const builtAtDiffers =
    running.builtAt > 0 && served.builtAt > 0 && served.builtAt !== running.builtAt;
  if (!buildDiffers && !builtAtDiffers) return { stale: false };
  const forced =
    running.builtAt > 0 &&
    running.builtAt < served.minBuiltAt &&
    served.builtAt >= served.minBuiltAt;
  const target = served.build !== UNSET_BUILD ? served.build : `built-${served.builtAt}`;
  return { stale: true, forced, target };
}

export interface StoredMark {
  target: string;
  at: number;
  /** Automatic reloads already spent on this target (reload guard only). */
  count?: number;
}

export function parseStoredMark(raw: string | null): StoredMark | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return null;
    const { target, at, count } = value as Record<string, unknown>;
    if (typeof target !== 'string' || target === '' || !isEpoch(at)) return null;
    return isEpoch(count) ? { target, at, count } : { target, at };
  } catch {
    return null;
  }
}

export interface UpdateState {
  stale: boolean;
  /** True once /version.json confirmed the difference. */
  confirmed: boolean;
  forced: boolean;
  /** Served build id, `built-<ms>`, or the unconfirmed signal name. */
  target: string | null;
  /** When this document was first found stale (epoch ms). */
  since: number;
  /** Something already failed for the user (a chunk would not load). */
  urgent: boolean;
}

export const CURRENT_STATE: UpdateState = Object.freeze({
  stale: false,
  confirmed: false,
  forced: false,
  target: null,
  since: 0,
  urgent: false,
});

export interface DecisionEnv {
  now: number;
  online: boolean;
  visible: boolean;
  /** When the page went hidden; null while visible. */
  hiddenSince: number | null;
  /** Length of the most recent hidden period (0 if never hidden). */
  lastHiddenMs: number;
  /** Any pointer/key input since the page last became visible. */
  inputSinceVisible: boolean;
  /** update-safety.isIdleForSilentApply() */
  idle: boolean;
  reloadGuard: StoredMark | null;
  /** The user dismissed the banner earlier in this tab session. */
  dismissed: boolean;
}

export type Decision =
  | { action: 'none' }
  | { action: 'apply' }
  | { action: 'banner'; dismissible: boolean };

/**
 * The single apply policy.
 *
 * - Offline: never reload, no banner; re-evaluated on `online`.
 * - Unconfirmed (a signal fired but /version.json could not confirm): banner
 *   only, never an automatic reload.
 * - Forced: banner without dismiss; reload at the first idle moment.
 * - Otherwise: reload silently only after the page sat hidden for
 *   HIDDEN_APPLY_MS and is idle (while still hidden, or on return before any
 *   input). A visible page is never reloaded under the user, and is only
 *   offered the banner once it has been stale for BANNER_AFTER_STALE_MS (or
 *   at once when something already failed). A dismissed banner stays away
 *   for the rest of the tab session. Both waiting periods come from
 *   `thresholds`, which is the defaults everywhere except a test host.
 * - Loop guard: one automatic reload per target per RELOAD_GUARD_MS, and at
 *   most MAX_AUTO_RELOADS per target in this tab; after that only the banner.
 */
export function decide(
  state: UpdateState,
  env: DecisionEnv,
  thresholds: Thresholds = DEFAULT_THRESHOLDS,
): Decision {
  if (!state.stale || state.target === null) return { action: 'none' };
  if (!env.online) return { action: 'none' };

  const guarded =
    env.reloadGuard !== null &&
    env.reloadGuard.target === state.target &&
    (env.now - env.reloadGuard.at < RELOAD_GUARD_MS ||
      (env.reloadGuard.count ?? 0) >= MAX_AUTO_RELOADS);
  const mayAuto = state.confirmed && env.idle && !guarded;

  if (state.forced) {
    return mayAuto ? { action: 'apply' } : { action: 'banner', dismissible: false };
  }

  const hiddenLongEnough = env.visible
    ? !env.inputSinceVisible && env.lastHiddenMs >= thresholds.hiddenApplyMs
    : env.hiddenSince !== null && env.now - env.hiddenSince >= thresholds.hiddenApplyMs;
  if (mayAuto && hiddenLongEnough) return { action: 'apply' };

  if (env.dismissed) return { action: 'none' };
  const overdue = state.urgent || env.now - state.since >= thresholds.bannerAfterStaleMs;
  return overdue ? { action: 'banner', dismissible: true } : { action: 'none' };
}
